import type { Model } from "@/schemas/model.schema";
import {
  CHAT_GATE,
  INTELLIGENCE_STAR_DIVISOR,
  MAX_RECOMMENDED,
  NEUTRAL_STAR,
  RECOMMENDATION_WEIGHTS,
  SPEED_STAR,
  STAR_MAX,
  STAR_MIN,
  VOICE_GATE,
} from "@/config/model-ratings.config";

/**
 * Pure, framework-free rating logic for the LLM picker. Kept separate from any
 * Vue component so it can be unit-tested in isolation (see model-ratings.test.ts).
 *
 * Star scale (PRD §4.2): intelligence is absolute; speed and cost are relative to
 * the shown set with absolute reality clamps at the corners. Because speed/cost
 * are relative, stars are computed for the WHOLE set at once via
 * `computeStarsForSet` — a single model has no speed/cost star out of context.
 */

/** Use-case bucket the user is filtering the picker by. */
export type Intent = "voice" | "chat" | "all";

export type Stars = {
  speed: number | null;
  intelligence: number | null;
  cost: number | null;
};

export type IntentRecommendation = {
  voice: boolean;
  chat: boolean;
};

/** Intents a model is editorially pinned for (`recommended_for`), in input order. */
export type PinnedIntents = ReadonlyArray<"voice" | "chat">;

/** Catalog `recommended_for` values that pin a model to a picker intent. */
const INTENT_BY_USE = new Map<string, "voice" | "chat">([
  ["realtime", "voice"],
  ["general", "chat"],
]);

/**
 * Blended per-1M-token price per model NAME, in the organization's currency.
 * Cost is NOT a property of the model — it comes from the org's rate deck
 * (`GET /organization/pricing`), so the picker injects this map. A model absent
 * from the map has no known price → no cost star. See `blendedPriceByModel` in
 * `@/utils/model-pricing`.
 */
export type PriceByModel = Map<string, number>;

/** Min/max of a metric across the set, used to normalise relative stars. */
type Range = { min: number; max: number };

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

function isPositive(value: number | null | undefined): value is number {
  return value != null && value > 0;
}

function rangeOf(values: number[]): Range {
  if (values.length === 0) return { min: 0, max: 0 };
  return { min: Math.min(...values), max: Math.max(...values) };
}

/**
 * 0..1 "goodness" of `value` within `range` (1 = best). When the set has no
 * spread (one model, or all equal) everyone is treated as equally best rather
 * than dividing by zero.
 */
function goodness(value: number, range: Range, lowerIsBetter: boolean): number {
  if (range.max === range.min) return 1;
  const t = (value - range.min) / (range.max - range.min);
  return lowerIsBetter ? 1 - t : t;
}

/**
 * The model's blended price (org currency, per 1M tokens) from the injected
 * rate-deck map, or null when the org has no price for it. Cost is org-specific
 * — it never comes off the model object itself.
 */
export function blendedPrice(model: Model, priceByModel: PriceByModel): number | null {
  return priceByModel.get(model.name) ?? null;
}

/**
 * Deprecation source of truth is `deprecation != null`. The legacy `deprecated`
 * boolean may still be set on older records, so it's treated as a fallback.
 */
export function isDeprecated(model: Pick<Model, "deprecation" | "deprecated">): boolean {
  return model.deprecation != null || model.deprecated === true;
}

/** Intelligence star — ABSOLUTE: `intelligence_index / 20`, clamped 0-5. */
function intelligenceStar(model: Model): number | null {
  const index = model.ratings?.intelligence_index;
  if (index == null) return null;
  return round1(clamp(index / INTELLIGENCE_STAR_DIVISOR, STAR_MIN, STAR_MAX));
}

/**
 * Speed star — RELATIVE to the set on a LOG scale (TTFT + tokens/sec, TTFT
 * dominant) with absolute reality clamps: a real-time TTFT never reads below the
 * floor, a slow one never above the ceiling. Log, not linear: TTFT and tps each
 * span ~2 orders of magnitude across the catalog, so a single fast outlier
 * (e.g. 1600 tps) would otherwise crush every normal model's tps signal to ~0.
 * See SPEED_STAR config. Ranges are already in log space (see computeStarsForSet).
 */
function speedStar(model: Model, ttftLogRange: Range, tpsLogRange: Range): number | null {
  const ttft = model.ratings?.ttft_ms;
  if (!isPositive(ttft)) return null;

  const ttftGood = goodness(Math.log10(ttft), ttftLogRange, true);
  const tps = model.ratings?.output_tps;
  const blend = isPositive(tps)
    ? SPEED_STAR.ttftWeight * ttftGood + SPEED_STAR.tpsWeight * goodness(Math.log10(tps), tpsLogRange, false)
    : ttftGood;

  let star = blend * STAR_MAX;
  // Floor a genuinely real-time model — but only if it also streams fast enough
  // to be voice-capable (tps gate), so "reads as fast" agrees with the voice
  // recommendation. A low-tps model with great TTFT gets its relative score, not
  // the floor. Missing tps → floor on TTFT alone (best effort).
  const tpsOkForFloor = !isPositive(tps) || tps >= SPEED_STAR.floorMinTps;
  if (ttft <= SPEED_STAR.floorTtftMs && tpsOkForFloor) {
    star = Math.max(star, SPEED_STAR.floorStar);
  }
  if (ttft > SPEED_STAR.ceilTtftMs) star = Math.min(star, SPEED_STAR.ceilStar);

  return round1(clamp(star, STAR_MIN, STAR_MAX));
}

/**
 * Cost star — RELATIVE to the set (log10 price, min-max, cheaper = more stars).
 * PURELY relative (no absolute clamp) because the price is in the organization's
 * currency (BRL, USD, …) — a fixed USD floor/ceiling would misfire the moment
 * the currency isn't USD. Cheapest available → 5★, priciest → 0★, which is
 * exactly what "cost relative to the other models" should mean.
 */
function costStar(model: Model, priceLogRange: Range, priceByModel: PriceByModel): number | null {
  const price = blendedPrice(model, priceByModel);
  if (!isPositive(price)) return null;

  const star = goodness(Math.log10(price), priceLogRange, true) * STAR_MAX;
  return round1(clamp(star, STAR_MIN, STAR_MAX));
}

/**
 * Computes the 0-5 speed/intelligence/cost stars for every model in `models`,
 * keyed by model name. Speed and cost are relative to THIS set, so they must be
 * computed together — that's why this takes the whole set rather than one model.
 * Any dimension is `null` when the model lacks that underlying data point.
 */
export function computeStarsForSet(
  models: Model[],
  priceByModel: PriceByModel = new Map()
): Map<string, Stars> {
  // Speed and cost are normalised in LOG space (both metrics span orders of
  // magnitude), so the ranges are built from log10 of the raw values.
  const ttftLogRange = rangeOf(
    models.map((m) => m.ratings?.ttft_ms).filter(isPositive).map((v) => Math.log10(v))
  );
  const tpsLogRange = rangeOf(
    models.map((m) => m.ratings?.output_tps).filter(isPositive).map((v) => Math.log10(v))
  );
  const priceLogRange = rangeOf(
    models
      .map((m) => blendedPrice(m, priceByModel))
      .filter(isPositive)
      .map((price) => Math.log10(price))
  );

  const stars = new Map<string, Stars>();
  for (const model of models) {
    stars.set(model.name, {
      speed: speedStar(model, ttftLogRange, tpsLogRange),
      intelligence: intelligenceStar(model),
      cost: costStar(model, priceLogRange, priceByModel),
    });
  }
  return stars;
}

function passesVoiceGate(model: Model): boolean {
  const ttft = model.ratings?.ttft_ms;
  const tps = model.ratings?.output_tps;
  if (ttft == null || tps == null) return false;
  return ttft <= VOICE_GATE.maxTtftMs && tps >= VOICE_GATE.minOutputTps;
}

function passesChatGate(model: Model): boolean {
  const intelligence = model.ratings?.intelligence_index;
  if (intelligence == null) return false;
  // Intelligence is the only chat REQUIREMENT; price is not a gate here, it
  // weighs into the ranking score instead (see recommendationScore), so an
  // expensive-but-smart model stays eligible and just ranks lower.
  return intelligence >= CHAT_GATE.minIntelligenceIndex;
}

/**
 * Whether `model` should be surfaced as recommended for voice / chat use cases.
 * Applies the gate first (on the raw measured numbers, per PRD §4.3), then lets
 * per-model overrides force the result either way.
 */
function recommendedIntents(model: Model): IntentRecommendation {
  const overrides = model.ratings?.overrides;

  const decide = (
    override: "include" | "exclude" | null | undefined,
    gate: boolean
  ): boolean => {
    if (override === "include") return true;
    if (override === "exclude") return false;
    return gate;
  };

  return {
    voice: decide(overrides?.voice, passesVoiceGate(model)),
    chat: decide(overrides?.chat, passesChatGate(model)),
  };
}

/**
 * Intents this model is pinned for by its editorial `recommended_for` uses:
 * "realtime" pins voice, "general" pins chat. Unknown uses are ignored. Pinning
 * only moves a model up the "Recommended" order, it never admits one the gate
 * rejected.
 */
export function pinnedIntents(model: Model): PinnedIntents {
  const pinned = new Set<"voice" | "chat">();
  for (const use of model.recommended_for ?? []) {
    const intent = INTENT_BY_USE.get(use);
    if (intent) pinned.add(intent);
  }
  return [...pinned];
}

/** Weights table key for an intent ("all" reuses the balanced blend). */
function weightsFor(intent: Intent) {
  return RECOMMENDATION_WEIGHTS[intent];
}

/**
 * The 0-5 recommendation score for a model under an intent: a weighted average
 * of its three stars, per RECOMMENDATION_WEIGHTS. A missing star counts as
 * NEUTRAL_STAR ("unknown, assume middling") — never renormalised, so a model
 * with a data gap can't leapfrog a complete one just by having fewer known
 * axes. This is the number that ranks the "Recommended" shortlist.
 */
export function recommendationScore(intent: Intent, stars: Stars | undefined): number {
  const w = weightsFor(intent);
  const s = stars?.speed ?? NEUTRAL_STAR;
  const i = stars?.intelligence ?? NEUTRAL_STAR;
  const c = stars?.cost ?? NEUTRAL_STAR;
  return w.speed * s + w.intelligence * i + w.cost * c;
}

/**
 * Comparator for the "Recommended" group: models pinned for the active intent
 * first (any pin counts under "all"), then by the weighted recommendation score
 * for the active intent (speed/intelligence/cost blended per
 * RECOMMENDATION_WEIGHTS), with the raw intelligence index as a stable tie-break.
 */
function recommendedComparator(intent: Intent, starsByModel: Map<string, Stars>) {
  const pinRank = (m: Model): number => {
    const pinned = pinnedIntents(m);
    if (pinned.length === 0) return 0;
    return intent === "all" || pinned.includes(intent) ? 1 : 0;
  };
  const scoreOf = (m: Model): number => recommendationScore(intent, starsByModel.get(m.name));
  const rawIntel = (m: Model): number => m.ratings?.intelligence_index ?? Number.NEGATIVE_INFINITY;

  return (a: Model, b: Model): number => {
    const byPin = pinRank(b) - pinRank(a);
    if (byPin !== 0) return byPin;
    return scoreOf(b) - scoreOf(a) || rawIntel(b) - rawIntel(a);
  };
}

/**
 * The shortlist shown under "Recommended": models passing the active intent's
 * eligibility gate, ranked by the weighted recommendation score, capped at
 * MAX_RECOMMENDED. Capping is what makes the recommendation a real shortlist
 * instead of "almost everything".
 */
export function recommendedModels(
  models: Model[],
  intent: Intent,
  starsByModel: Map<string, Stars>
): Model[] {
  const picked = models.filter((model) => {
    const rec = recommendedIntents(model);
    if (intent === "voice") return rec.voice;
    if (intent === "chat") return rec.chat;
    return rec.voice || rec.chat;
  });
  picked.sort(recommendedComparator(intent, starsByModel));
  return picked.slice(0, MAX_RECOMMENDED);
}
